import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { hashPassword } from '@/lib/passwordHash'
import {
  PLATFORM_SESSION_ENDED,
  PlatformError,
  isMfaChallenge,
  pGet,
  pPost,
  type PlatformMe,
  type PlatformMfaChallenge,
} from './api'

/**
 * Who is signed in to the portal, and whether the portal is here at all.
 *
 * A separate provider from `@/lib/auth` on purpose, and the carrier one never
 * mounts on this host (see `src/main.tsx`): the portal does not call
 * `/auth/me`, holds no carrier session and has nothing to read if somebody
 * arrives here with a carrier cookie in the jar.
 *
 * **`servedHere` is the host check.** The API answers every `/platform` path
 * with 404 anywhere but the portal's own host
 * (`app/core/platform_auth.require_platform_host`), so the first call
 * distinguishes three things rather than two: 200 is a session, 401 is the
 * portal with nobody signed in, and **404 is not the portal** - somebody has
 * opened `/admin` on a carrier's subdomain, and the answer is to send them to
 * that carrier's own sign-in page rather than to a login form that could never
 * work. The host is decided by the API, not guessed at from
 * `window.location`: one place decides, and a second place guessing is how the
 * two come to disagree.
 */

interface Ctx {
  me: PlatformMe | null
  loading: boolean
  /** False when this host does not serve the portal. */
  servedHere: boolean
  signIn: (email: string, password: string) => Promise<PlatformMe | PlatformMfaChallenge>
  verifyMfa: (body: { challenge: string; code?: string; recoveryCode?: string }) => Promise<PlatformMe>
  signOut: () => Promise<void>
  setMe: (me: PlatformMe | null) => void
  refresh: () => Promise<void>
}

const PlatformAuthContext = createContext<Ctx | null>(null)

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<PlatformMe | null>(null)
  const [loading, setLoading] = useState(true)
  const [servedHere, setServedHere] = useState(true)

  const load = useCallback(async () => {
    try {
      setMe(await pGet<PlatformMe>('/auth/me'))
      setServedHere(true)
    } catch (e) {
      setMe(null)
      if (e instanceof PlatformError && e.status === 404) setServedHere(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // There is no refresh token here, so an expired session is simply over.
  useEffect(() => {
    const ended = () => setMe(null)
    window.addEventListener(PLATFORM_SESSION_ENDED, ended)
    return () => window.removeEventListener(PLATFORM_SESSION_ENDED, ended)
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    // Hashed here, with the email as the salt, exactly as a carrier sign-in
    // does: the API accepts nothing else (app/core/client_hash.py).
    const hashed = await hashPassword(password, email)
    const next = await pPost<PlatformMe | PlatformMfaChallenge>('/auth/login', {
      email,
      password: hashed,
    })
    // A challenge is not a session. Setting `me` from it would show the portal
    // to somebody who has proved only half of what is required.
    if (!isMfaChallenge(next)) setMe(next)
    return next
  }, [])

  const verifyMfa = useCallback(
    async (body: { challenge: string; code?: string; recoveryCode?: string }) => {
      const next = await pPost<PlatformMe>('/auth/mfa', body)
      setMe(next)
      return next
    },
    [],
  )

  const signOut = useCallback(async () => {
    try {
      await pPost('/auth/logout')
    } finally {
      setMe(null)
    }
  }, [])

  return (
    <PlatformAuthContext.Provider value={{ me, loading, servedHere, signIn, verifyMfa, signOut, setMe, refresh: load }}>
      {children}
    </PlatformAuthContext.Provider>
  )
}

export function usePlatformAuth(): Ctx {
  const ctx = useContext(PlatformAuthContext)
  if (!ctx) throw new Error('usePlatformAuth outside PlatformAuthProvider')
  return ctx
}
